<template lang="pug">
    section.product-step
        el-form-item(:label="$t('Name')" prop="name")
            el-input(v-model="form.name")
        .flex-row(:gutter="60")
            el-form-item.grow(:label="$t('Product-Nr.')" prop="nr")
                el-input(v-model="form.nr")
            el-form-item(:label="$t('Type')" prop="type")
                el-select.is-full-width(placeholder="" v-model="form.type" :disabled="typeLocked")
                    el-option(
                        v-for="(item, key) in productTypes"
                        :key="key"
                        :label="$t(item.label)"
                        :value="item.value"
                    )
            el-form-item(:label="$t('Status')" prop="status")
                el-select.is-full-width(placeholder="" v-model="form.status")
                    el-option(
                        v-for="(item, key) in options.status.items"
                        :key="key"
                        :label="$t(item.label)"
                        :value="item.value"
                    )
        //- Condition + Brand in their own row — the previous 5-column row
        //- was too cramped. Neither means anything for a coupon or a ticket: there is no
        //- second-hand admission and no manufacturer.
        .flex-row(v-if="sellsPhysicalGoods" :gutter="60")
            el-form-item.grow(:label="$t('Condition')" prop="condition")
                el-select.is-full-width(placeholder="" v-model="form.condition")
                    el-option(
                        v-for="(item, key) in options.condition.items"
                        :key="key"
                        :label="$t(item.label)"
                        :value="item.value"
                    )
            el-form-item.grow(:label="$t('Brand')" prop="brand")
                el-input(v-model="form.brand" maxlength="70" show-word-limit)

        //- § 312g BGB: opt the product out of returns (custom-made,
        //- perishables, daily newspapers, sealed health items, lottery
        //- tickets, …). Default ON.
        el-form-item(prop="returnable")
            el-switch(
                :value="form.returnable !== false"
                @change="value => $set(form, 'returnable', !!value)"
                :active-text="$t('Can be revoked (§ 312g BGB)')"
            )
            .help {{ revocationHint }}
        transition(name="slide-decent-vertical")
            .flex-row(v-if="form.type === 'coupon' && form.coupon")
                el-form-item.grow(:label="$t('Coupon amount')" prop="coupon.amount")
                    .flex-row
                        transition(name="slide-left")
                            //- el-input.grow(
                            //-     v-if="couponAmountType === 'fixed'"
                            //-     type="number"
                            //-     ref="couponAmount"
                            //-     v-model="form.coupon.amount"
                            //- )
                            el-input-number(
                                v-if="couponAmountType === 'fixed'"
                                v-model="form.coupon.amount"
                                controls-position="right"
                                :min="0.01"
                                :step="0.01"
                                :step-strictly="true"
                                ref="couponAmount"
                            )
                        el-select.is-full-width(placeholder="" v-model="couponAmountType")
                            el-option(
                                :label="$t('automatically, based on the price')"
                                value="auto"
                            )
                            el-option(
                                :label="$t('fixed amount')"
                                value="fixed"
                            )
                el-form-item(label=" " prop="coupon.discard_remaining_amount")
                    el-switch(
                        :active-text="$t('Discard the remaining amount')"
                        v-model="form.coupon.discard_remaining_amount"
                    )
                el-form-item(label=" " prop="coupon.discard_the_following_coupons")
                    el-switch(
                        :active-text="$t('Discard the following coupons')"
                        v-model="form.coupon.discard_the_following_coupons"
                    )
        el-form-item(:label="$t('Teaser')")
            quill-editor(
                v-model="form.teaser"
                :options="quillConfig"
            )
        el-form-item(:label="$t('Full Description')")
            quill-editor(
                v-model="form.description"
                :options="quillConfig"
            )

        .flex-row
            el-form-item.grow(:label="$t('Categories')")
                //- :value-consists-of="'ALL'"
                //- :flat="true"
                Treeselect(
                    v-model="categories"
                    placeholder=""
                    :multiple="true"
                    :options="categoryOptions"
                    :default-expand-level="Infinity"
                    :close-on-select="false"
                    :open-on-focus="true"
                    :searchable="true"
                    :normalizer="node => ({ label: node.name, ...node })"
                    :clearable="false"
                    :always-open="false"
                )
                    label(
                        slot="option-label"
                        slot-scope="{ node }"
                    ) {{ node.raw.name }}
                    div(
                        slot="value-label"
                        slot-scope="{ node }"
                    ) {{ getBreadcrumb(node.raw) }}
            el-form-item.pos-favorite(
                v-if="enabledExtensions.includes('shop:point-of-sale')"
                label=" "
                prop="pos_favorite"
            )
                el-button(
                    :plain="!form.pos_favorite"
                    :type="form.pos_favorite ? 'primary' : 'info'"
                    icon="el-icon-fa-bookmark fas"
                    @click="form.pos_favorite = !form.pos_favorite"
                ) {{ $t('Point Of Sale Favorite') }}

        el-form-item(:label="$t('Media')" prop="media")
            draggable(
                v-if="form.media && form.media.length"
                v-model="form.media"
                draggable="li.media"
            )
                transition-group(name="fade" tag="ul" class="media no-style-list")
                    ProductMedium(
                        v-for="(media, index) in form.media"
                        :key="media.id"
                        :media="media"
                        :form="form"
                        scope="stepper"
                    )
            el-upload.plus-box(
                ref="upload"
                drag multiple :action="uploadEndpoint"
                accept="image/jpg, image/jpeg, image/png, video/mp4"
                :before-upload="validateUpload"
                :on-change="updateUploadHeaders"
                :on-success="uploadSucceed"
                :on-error="uploadFailed"
                :headers="uploadHeaders"
                :data="uploadData"
            )
                i.el-icon-plus
                // i.el-icon-upload.el-icon-fa-cloud-upload-alt.fas
                .el-upload__text(v-html="$t('upload.drop.plural')")
                //- Integrated into the dropzone: opens the library / webcam dialog
                //- instead of the file picker (stop the dragger's click → file dialog).
                .media-add__actions
                    button.media-add__library(
                        type="button"
                        @click.stop.prevent="showLibraryDialog = true"
                    )
                        i.el-icon-picture-outline
                        span {{ $t('Select from library') }}
                    button.media-add__library.media-add__webcam(
                        type="button"
                        @click.stop.prevent="showWebcamDialog = true"
                    )
                        i.el-icon-fa-camera.fas
                        span {{ $t('Take photo') }}
                .el-upload__tip(slot="tip") {{ $t('upload.validation', { filetypes: 'jpg/png/mp4', size: '12mb' }) }}

            //- Single pick, like the content image fields: the multi-select mode selects a
            //- FILE, which cannot express picking one version out of a file's stack, and the
            //- gallery accepts repeated picks anyway. onProductLibrarySelected normalises a
            //- single value, so nothing downstream changes.
            image-library-dialog(
                :visible.sync="showLibraryDialog"
                :multiple="false"
                selection-type="media"
                @select="onProductLibrarySelected"
            )

            webcam-capture-dialog(
                :visible.sync="showWebcamDialog"
                @captured="webcamCaptured"
            )
</template>

<i18n>
{
    "de": {
        "Product": "Produkt",
        "Coupon": "Gutschein",
        "Condition": "Zustand",
        "New": "Neu",
        "Refurbished": "Generalüberholt",
        "Used": "Gebraucht",
        "Brand": "Marke",
        "Coupon amount": "Gutscheinbetrag",
        "Discard the following coupons": "Nachfolgende Gutscheine verwerfen",
        "Discard the remaining amount": "Restbetrag verwerfen",
        "fixed amount": "Fester Betrag",
        "automatically, based on the price": "Automatisch, basierend auf dem Preis",
        "Product-Nr.": "Produkt-Nr.",
        "Teaser": "Anreißer",
        "Full Description": "Vollständige Beschreibung",
        "Categories": "Kategorien",
        "Point Of Sale Favorite": "Kassenfavorit",
        "Media": "Medien",
        "Select from library": "Aus Mediathek wählen",
        "Take photo": "Foto aufnehmen",
        "Can be revoked (§ 312g BGB)": "kann widerrufen werden (gem. § 312g BGB)",
        "Events with a fixed date are excluded from the right of withdrawal (§ 312g Abs. 2 Nr. 9 BGB). Turn this on only if you want to take tickets back voluntarily.": "Veranstaltungen mit festem Termin sind vom Widerrufsrecht ausgenommen (§ 312g Abs. 2 Nr. 9 BGB). Nur einschalten, wenn Tickets freiwillig zurückgenommen werden sollen.",
        "Value vouchers are not tied to a date and are normally revocable. Turn this off only if you have a reason to exclude them.": "Wertgutscheine sind an keinen Termin gebunden und in der Regel widerrufbar. Nur ausschalten, wenn es einen Grund für den Ausschluss gibt.",
        "Turn off for custom-made goods, perishables, daily newspapers, sealed health items, lottery tickets, etc. Excluded products cannot be selected in the return flow.": "Für Maßanfertigungen, verderbliche Ware, Tageszeitungen, versiegelte Hygiene-Artikel, Lottoscheine etc. ausschalten. Ausgeschlossene Produkte können nicht widerrufen werden."
    }
}
</i18n>

<script>
import _ from 'lodash';
import axios from 'axios';

import draggable from 'vuedraggable';
import uniqid from 'uniqid';

import { ensureFreshToken } from '@/helpers'

import store from '@/store'
import pinia from '@/pinia';
import { useAuthStore, useSetupStore, useShopProductStore } from '@/stores';
import PageApi from '@/store/api/cms/page';

import Treeselect from '@riophae/vue-treeselect';
import NestedSet from '@/nested-set-lib';
import quillConfig from '@/quill/config';
import { withTextGenerationContext } from '@/quill/toolbar/textGeneration';

import ProductMedium from '@/extensions/shop/views/Product/Form/Partials/ProductMedium';
import WebcamCaptureDialog from '@/extensions/shop/views/Product/Form/Partials/WebcamCaptureDialog';
import ImageLibraryDialog from '@/components/Page/Sections/Partials/ImageLibraryDialog';

export default {
    name: 'ProductStep',
    props: {
        form: {
            type: Object,
            required: true
        },
        rules: {
            type: Object,
            required: true
        },
        // Set when the stepper was opened from an event to sell tickets for it — the type is the
        // whole reason the drawer is open, so it is not up for changing.
        typeLocked: {
            type: Boolean,
            default: false
        }
    },
    data() {
        quillConfig.modules.link.pages = this.getPageOptions;
        return {
            // Unsaved by design: only the name typed so far.
            quillConfig: withTextGenerationContext(quillConfig, () => ({ title: this.form.name })),
            quillPostLinksFeatureEnabled: false,
            uploadData: {
                'context[type]': 'product'
            },
            couponAmountType: 'auto', // possible values: 'auto', 'fixed'
            showLibraryDialog: false,
            showWebcamDialog: false,
        };
    },
    components: {
        Treeselect,
        ProductMedium,
        draggable,
        ImageLibraryDialog,
        WebcamCaptureDialog
    },
    watch: {
        'form.name'(value) {
            if (!value) {
                // || _.get(this.newItemDialog.form, 'type.key') === 'Redirect'
                this.form.nr = '';
                return;
            }

            const requestedNr = _.kebabCase(value);
            let nr = requestedNr;

            // var i = 0;
            // const usedNrs = ...
            // while (usedNrs.includes(nr)) {
            //     ++i;
            //     nr = `${requestedNr}-${i}`;
            // }

            this.form.nr = nr;
        },
        'form.type'(value) {
            let coupon
            // let manageStock = true
            if (value === 'coupon') {
                // manageStock = false
                coupon = {
                    id: uniqid('new:'),
                    name: undefined,
                    description: undefined,
                    code: null,
                    position: undefined,
                    type: 'fixed',
                    max_uses: 1,
                    max_uses_per_customer: undefined,
                    percentage: undefined,
                    amount: undefined,
                    start_at: undefined,
                    end_at: undefined,
                    // pivot: { uses: 0 },
                    discard_the_following_coupons: false,
                    discard_remaining_amount: false,
                };

                // this.$set(this.form, 'articleQty', 0);
                // this.$set(this.form, 'articleStock', null);
                // this.$set(this.form, 'propertyPivots', []);
            }

            // this.$set(this.form, 'manage_stock', manageStock);
            this.$set(this.form, 'coupon', coupon);

            // § 312g Abs. 2 Nr. 9 BGB excludes leisure services tied to a specific date from the
            // right of withdrawal, which is what an event ticket is - its dates are the product.
            // Defaulted off rather than hidden, because a merchant may still choose to take tickets
            // back voluntarily. A coupon is NOT covered by that exception, so it stays revocable.
            //
            // Set on every branch, not just the event one: switching away from a ticket has to put
            // the default back, or the exclusion silently sticks to the next type.
            this.$set(this.form, 'returnable', value !== 'event');
        },
        couponAmountType(value) {
            if (value === 'auto') {
                this.form.coupon.amount = undefined;
            } else if (value === 'fixed') {
                setTimeout(() => this.$refs.couponAmount.focus(), 100)
            }
        }
    },
    computed: {
        uploadHeaders() {
            return {
                authorization: `Bearer ${useAuthStore().token}`,
                accept: 'application/json',
            }
        },
        categories: {
            get() {
                const categories = _.get(this.form, 'categories') || [];
                return _.map(categories, 'id');
            },
            set(categories) {
                this.$set(
                    this.form,
                    'categories',
                    categories.map(id => ({ id }))
                );
            }
        },
        categoryOptions() {
            let tree = [];
            let items = _.get(this.options, 'category.items') || [];

            if (items.length) {
                items = _.cloneDeep(items);
                tree = NestedSet.createTree(items);
            }

            return tree;
        },
        uploadEndpoint() {
            return `${this.setupStore.uploadEndpoint}/public/${this.activeCms.id}/shop`
        },
        setupStore() {
            return useSetupStore();
        },
        shopProductStore() {
            return useShopProductStore(pinia);
        },
        activeCms() {
            return this.setupStore.activeCms;
        },
        enabledExtensions() {
            return this.setupStore.enabledExtensions;
        },
        options() {
            return this.shopProductStore.options;
        },
        productTypes() {
            return this.shopProductStore.productTypes;
        },
        /**
         * Whether this product is an actual thing. A coupon and an event ticket are entitlements:
         * they have no condition, no manufacturer, nothing to ship.
         */
        sellsPhysicalGoods() {
            return !['coupon', 'event'].includes(this.form.type);
        },
        /**
         * Why the switch is where it is, in terms of the type being sold. The generic wording about
         * perishables and newspapers says nothing useful under a ticket.
         */
        revocationHint() {
            if (this.form.type === 'event') {
                return this.$t('Events with a fixed date are excluded from the right of withdrawal (§ 312g Abs. 2 Nr. 9 BGB). Turn this on only if you want to take tickets back voluntarily.');
            }

            if (this.form.type === 'coupon') {
                return this.$t('Value vouchers are not tied to a date and are normally revocable. Turn this off only if you have a reason to exclude them.');
            }

            return this.$t('Turn off for custom-made goods, perishables, daily newspapers, sealed health items, lottery tickets, etc. Excluded products cannot be selected in the return flow.');
        }
    },
    methods: {
        async getPagesPaginated(searchString = '') {
            const categories = _.get(this.activeCms, 'config.template.categories') || [];
            let data = {
                filters: [{
                    "key": "title",
                    "like": searchString
                },
                {
                    "key": "cms",
                    "match": this.activeCms.id
                },
                {
                    "in": categories.map(el => el.key),
                    "key": "category_keys"
                }],
                variables: {
                    order: 'created_at',
                    direction: 'desc',
                    withCategory: true
                },
            };

            let pageData;
            await PageApi.fetchPagesPaginated(data).then(data => {
                pageData = data.items;
            });
            return pageData;
        },
        getBreadcrumb(category) {
            let breadcrumb = category.name;

            const items = _.get(this.options, 'category.items') || [];
            let ancestors = NestedSet.getAncestors(items, category);

            const validatedItems = NestedSet.validate(_.cloneDeep(items));
            ancestors = ancestors.filter(el => validatedItems.valid.IDs.includes(el.id));

            if (ancestors && ancestors.length) {
                ancestors = _.map(ancestors, 'name');
                breadcrumb = ancestors.join(' > ');
                breadcrumb += ` > ${category.name}`;
            }

            return breadcrumb;
        },
        async updateUploadHeaders() {
            // The computed uploadHeaders reads the store token; make sure it is fresh.
            await ensureFreshToken();
        },
        async validateUpload(file) {
            await this.updateUploadHeaders();
            if (file.size / 1024 / 1024 > 12) {
                // this.$message.error('Avatar picture size can not exceed 2MB!');
                this.$notify.error({
                    title: this.$t('upload.sizeExceeded'),
                    message: this.$t('upload.max', { size: '12mb' }),
                    iconClass: 'fas el-icon-fa-times',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000
                });
                // Reject, never `return false`: an async before-upload always returns a promise,
                // and Element aborts only on rejection - a resolved `false` uploads anyway.
                return Promise.reject();
            }
        },
        async uploadSucceed(response, data) {
            const imageMimeTypes = ['image/jpeg', 'image/gif', 'image/png', 'image/webp'];
            const videoMimeTypes = ['video/mp4', 'video/mpeg', 'video/webm', 'video/quicktime'];

            let type = 'file';
            if (imageMimeTypes.includes(response.mime)) type = 'image';
            else if (videoMimeTypes.includes(response.mime)) type = 'video';

            let image, video, file;
            switch (type) {
                case 'file':
                    file = {
                        id: response.id,
                        name: response.name,
                        mime: response.mime,
                        type: 'original',
                        upload: true
                    };
                    break;

                case 'image':
                    image = {
                        id: uniqid('new:'),
                        width: response.dimensions.width,
                        height: response.dimensions.height,
                        source: {
                            id: response.id,
                            name: response.name,
                            mime: response.mime,
                            type: 'original',
                            upload: true
                        }
                    };
                    break;

                case 'video':
                    video = {
                        id: uniqid('new:'),
                        width: response.dimensions.width,
                        height: response.dimensions.height,
                        source: {
                            id: response.id,
                            name: response.name,
                            mime: response.mime,
                            type: 'original',
                            upload: true
                        }
                    };
                    break;
            }

            const media = {
                id: uniqid('new:'),
                type,
                scope: 'product',
                position: this.form.media.length + 1,
                settings: {
                    show: {
                        list: !this.form.media.filter(el => el.type === 'image').length && type === 'image',
                        detail: true,
                        highlight: !this.form.media.filter(el => el.type === 'image').length && type === 'image',
                        disabled: false
                    }
                },
                image,
                video,
                file
            };

            setTimeout(() => {
                this.$refs.upload.uploadFiles = this.$refs.upload.uploadFiles.filter(el => el.uid !== data.uid);
            }, 1000);

            this.form.media.push(media);

            await  this.updateUploadHeaders();
        },
        async uploadFailed(err, file) {
            await this.updateUploadHeaders();

            this.$notify.error({
                title: this.$t('upload.notUploaded'),
                message: _.get(file, 'name'),
                iconClass: 'fas el-icon-fa-times',
                position: 'bottom-left',
                customClass: 'dark',
                duration: 8000
            });
        },
        buildMediaFromUploadResponse(response) {
            const imageMimeTypes = ['image/jpeg', 'image/gif', 'image/png', 'image/webp'];
            const videoMimeTypes = ['video/mp4', 'video/mpeg', 'video/webm', 'video/quicktime'];

            let type = 'file';
            if (imageMimeTypes.includes(response.mime)) type = 'image';
            else if (videoMimeTypes.includes(response.mime)) type = 'video';

            let image, video, file;
            switch (type) {
                case 'file':
                    file = {
                        id: response.id,
                        name: response.name,
                        preferred_name: response.preferred_name,
                        mime: response.mime,
                        type: 'original',
                        upload: true
                    };
                    break;

                case 'image':
                    image = {
                        id: uniqid('new:'),
                        width: response.dimensions.width,
                        height: response.dimensions.height,
                        source: {
                            id: response.id,
                            name: response.name,
                            preferred_name: response.preferred_name,
                            mime: response.mime,
                            type: 'original',
                            upload: true
                        }
                    };
                    break;

                case 'video':
                    video = {
                        id: uniqid('new:'),
                        width: response.dimensions.width,
                        height: response.dimensions.height,
                        source: {
                            id: response.id,
                            name: response.name,
                            preferred_name: response.preferred_name,
                            mime: response.mime,
                            type: 'original',
                            upload: true
                        }
                    };
                    break;
            }

            return {
                id: uniqid('new:'),
                type,
                scope: 'product',
                position: this.form.media.length + 1,
                settings: {
                    show: {
                        list: !this.form.media.filter(el => el.type === 'image').length && type === 'image',
                        detail: true,
                        highlight: !this.form.media.filter(el => el.type === 'image').length && type === 'image',
                        disabled: false
                    }
                },
                image,
                video,
                file
            };
        },
        // Webcam capture dialog hands back a raw Blob (image/jpeg) — upload it the
        // same way the main tab does: FormData + axios, reusing uploadEndpoint/
        // uploadHeaders so it goes through the same auth path as the dropzone. The
        // response shape is identical, so it reuses the same builder.
        async webcamCaptured(blob) {
            await this.updateUploadHeaders();

            const formData = new FormData();
            formData.append('file', blob, `webcam-${Date.now()}.jpg`);
            formData.append('context[type]', 'product');

            try {
                const response = await axios.post(this.uploadEndpoint, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        authorization: this.uploadHeaders.authorization
                    }
                });
                this.form.media.push(this.buildMediaFromUploadResponse(response.data));
            } catch (err) {
                this.$notify.error({
                    title: this.$t('upload.notUploaded'),
                    message: this.$t('Take photo'),
                    iconClass: 'fas el-icon-fa-times',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000
                });
            } finally {
                await this.updateUploadHeaders();
            }
        },
        // Add one or more images chosen from the media library as new product media —
        // the upload alternative. The library payload (new:file-library: + source) is
        // persisted by the product backend's library-selection branch on Save.
        onProductLibrarySelected(selection) {
            const picked = Array.isArray(selection) ? selection : [selection];
            picked.filter(Boolean).forEach((entry) => {
                // The picker offers images and videos, and says which it handed back.
                const kind = _.get(entry, 'kind') === 'video' ? 'video' : 'image';
                const payload = _.get(entry, 'payload', entry);
                const hasImage = (this.form.media || []).some(item => item.type === 'image');
                // Only an image can be the list/highlight picture, so a video never
                // claims those slots.
                const showsAsCover = kind === 'image' && !hasImage;

                const media = {
                    id: uniqid('new:'),
                    type: kind,
                    scope: 'product',
                    position: (this.form.media || []).length + 1,
                    settings: { show: { list: showsAsCover, detail: true, highlight: showsAsCover, disabled: false } },
                };

                if (kind === 'video') media.video = payload;
                else media.image = { ...payload, type: 'product-image' };

                this.form.media.push(media);
            });
        },
        getPageOptions() {
            let menus = _.get(this.activeCms, 'config.template.menus', []);
            menus = _.cloneDeep(menus).sort((a, b) => _.get(a, 'position', 0) - _.get(b, 'position', 0));

            const menuOptions = _.get(this.options, 'menu.items') || [];
            // const visibleOptgroups = menuOptions.filter(el => el.menuItems.length).length > 1;

            let tree = [];
            menus.forEach(menu => {
                let dir = { folder: menu.name, children: [] };
                let children = [];

                const menuOption = _.find(menuOptions, { key: menu.key });
                let items = _.get(menuOption, 'menuItems', []);

                if (items.length) {
                    items = _.cloneDeep(items);
                    children = NestedSet.createTree(items);
                }

                dir.children = children;
                if (children.length) tree.push(dir);
            });

            return tree;
        }
    },
    created() {
        //>>> add quill config depending on template features
        const features = _.get(this.activeCms, 'config.template.features') || []
        this.quillPostLinksFeatureEnabled = !!features.find(el => el === 'quill:post-links');
        if (this.quillPostLinksFeatureEnabled) {
            if (!quillConfig.modules.link.types.includes('posts')) {
                quillConfig.modules.link.types.splice(1, 0, 'posts');
            }
            quillConfig.modules.link.posts = this.getPagesPaginated;
        }
        //<<<
    },
};
</script>

<style lang="sass" scoped>
.help
    display: block
    margin-top: 4px
    font-size: 12px
    line-height: 1.4
    color: var(--color-text-muted, #909399)

// Combined affordances: a library shortcut and a webcam shortcut, side by side
// inside the dropzone. Each opens its own dialog (the @click.stop in the template
// keeps the file picker from firing).
.media-add__actions
    display: flex
    align-items: center
    gap: 8px
    flex-wrap: wrap
    justify-content: center
    margin-top: 8px
.media-add__library
    display: inline-flex
    align-items: center
    gap: 6px
    padding: 4px 12px
    border: 1px solid #dcdfe6
    border-radius: 4px
    background: #fff
    font-size: 12px
    line-height: 1.2
    color: #6E26FF
    white-space: nowrap
    cursor: pointer
    transition: border-color 0.15s ease, background-color 0.15s ease
    &:hover
        border-color: #6E26FF
        background-color: rgba(110, 38, 255, 0.06)
</style>
